import { useLayoutEffect, useRef, useState } from 'react';

import ContentContainer from 'components/content-container/ContentContainer';
import ScrubberTrack from './ScrubberTrack';
import ScrubberHandle from './ScrubberHandle';

import { IKvinnehistorieTimelinePage_Labels, IKvinnehistorieTimelinePage_Scrubber } from 'source/NFR.types';

// Min width per decade tick to keep year labels readable; below this we use the stepper.
const MIN_TICK_WIDTH = 42;

// Space the heading + gap take in rail mode, reserved so the ticks fit alongside it.
const HEADING_RESERVE = 120;

const RAIL_MIN_VIEWPORT = 768;

interface KvinnehistorieScrubberProps extends IKvinnehistorieTimelinePage_Scrubber {
  labels?: IKvinnehistorieTimelinePage_Labels;
  activeYear?: string;
  onNavigate?: (anchorId: string) => void;
  onScrub?: (anchorId: string) => void;
  onRailFitsChange?: (railFits: boolean) => void;
}

const KvinnehistorieScrubber: React.FC<KvinnehistorieScrubberProps> = ({
  decades,
  labels,
  activeYear,
  onNavigate,
  onScrub,
  onRailFitsChange
}) => {
  const railAreaRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const [railFits, setRailFits] = useState(false);
  const onRailFitsChangeRef = useRef(onRailFitsChange);
  onRailFitsChangeRef.current = onRailFitsChange;

  const decadeTicks = decades ?? [];

  // Desktop only: show the horizontal rail while every decade fits at a readable width;
  // otherwise drop to the stepper. Below the mobile breakpoint we always use the stepper,
  // regardless of how few decades there are.
  useLayoutEffect(() => {
    const region = regionRef.current;
    // Measure the parent, not the region: the region's width changes with the heading, which
    // would make the rail toggle flicker.
    const inner = region?.parentElement;
    if (!region || !inner) return;

    const mql = window.matchMedia(`(min-width: ${RAIL_MIN_VIEWPORT}px)`);
    const evaluate = () => {
      const availableForTicks = inner.clientWidth - HEADING_RESERVE;
      const fits = mql.matches && decadeTicks.length > 0 && availableForTicks >= decadeTicks.length * MIN_TICK_WIDTH;
      setRailFits(fits);
      onRailFitsChangeRef.current?.(fits);
    };

    evaluate();
    const observer = new ResizeObserver(evaluate);
    observer.observe(inner);
    mql.addEventListener('change', evaluate);
    return () => {
      observer.disconnect();
      mql.removeEventListener('change', evaluate);
    };
  }, [decadeTicks.length]);

  const activeDecade = activeYear ? Math.floor(parseInt(activeYear, 10) / 10) * 10 : NaN;
  const activeDecadeIndex = Number.isNaN(activeDecade)
    ? -1
    : decadeTicks.findIndex(tick => parseInt(tick.title ?? '', 10) === activeDecade);

  const previousDecade = activeDecadeIndex > 0 ? decadeTicks[activeDecadeIndex - 1] : undefined;
  const nextDecade =
    activeDecadeIndex >= 0 && activeDecadeIndex < decadeTicks.length - 1
      ? decadeTicks[activeDecadeIndex + 1]
      : undefined;

  if (decadeTicks.length === 0) {
    return (
      <div className="kvinnehistorie-scrubber" aria-hidden="true">
        <ContentContainer className="kvinnehistorie-scrubber--inner">
          <span className="kvinnehistorie-scrubber--heading">{labels?.heading || 'Tidslinje'}</span>
        </ContentContainer>
      </div>
    );
  }

  return (
    <nav className="kvinnehistorie-scrubber" aria-label={labels?.scrubberAriaLabel || 'Tidslinjenavigasjon'}>
      <ContentContainer className="kvinnehistorie-scrubber--inner">
        {railFits && <span className="kvinnehistorie-scrubber--heading">{labels?.heading || 'Tidslinje'}</span>}
        <div className="kvinnehistorie-scrubber--region" ref={regionRef}>
          {railFits ? (
            <div className="kvinnehistorie-scrubber--rail-area" ref={railAreaRef}>
              <ScrubberTrack
                items={decadeTicks}
                className="-decades"
                label={labels?.decadeNavAriaLabel || 'Naviger etter tiår'}
                activeIndex={activeDecadeIndex}
                onNavigate={onNavigate}
              />
              <ScrubberHandle
                containerRef={railAreaRef}
                activeIndex={activeDecadeIndex >= 0 ? activeDecadeIndex : 0}
                ariaLabel={labels?.scrubHandle || 'Skrubb tidslinjen'}
                labels={decadeTicks.map(tick => tick.title ?? '')}
                onSelect={index => {
                  const tick = decadeTicks[index];
                  if (tick && onNavigate) onNavigate(tick.anchorId);
                }}
                onScrub={index => {
                  const tick = decadeTicks[index];
                  if (tick && onScrub) onScrub(tick.anchorId);
                }}
              />
            </div>
          ) : (
            <div className="kvinnehistorie-scrubber--stepper">
              <button
                type="button"
                className="kvinnehistorie-scrubber--stepper-button"
                aria-label={labels?.previousDecade || 'Forrige tiår'}
                disabled={!previousDecade}
                onClick={() => previousDecade && onNavigate && onNavigate(previousDecade.anchorId)}
              >
                <span className="kvinnehistorie-scrubber--stepper-arrow -left" aria-hidden="true" />
              </button>
              <span className="kvinnehistorie-scrubber--stepper-label" aria-live="polite">
                {Number.isNaN(activeDecade) ? '' : activeDecade}
              </span>
              <button
                type="button"
                className="kvinnehistorie-scrubber--stepper-button"
                aria-label={labels?.nextDecade || 'Neste tiår'}
                disabled={!nextDecade}
                onClick={() => nextDecade && onNavigate && onNavigate(nextDecade.anchorId)}
              >
                <span className="kvinnehistorie-scrubber--stepper-arrow -right" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </ContentContainer>
    </nav>
  );
};

KvinnehistorieScrubber.displayName = 'KvinnehistorieScrubber';

export default KvinnehistorieScrubber;
